import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { staffClient } from 'clients/staff';
import './style.css';

interface Site {
    id: number;
    name: string;
    location?: string;
    address?: string;
    status: 'active' | 'completed' | 'upcoming';
    type?: string;  // 서버에서 사용하는 type 필드
    progressRate?: string | number;
    progress?: number;
}

interface SiteSearchBarProps {
    currentSiteId?: string;
    onSiteChange?: (siteId: number) => void;
}

const SiteSearchBar: React.FC<SiteSearchBarProps> = ({ currentSiteId, onSiteChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    
    const [sites, setSites] = useState<Site[]>([]);
    const [filteredSites, setFilteredSites] = useState<Site[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchSites();
    }, []);

    useEffect(() => {
        // 클릭 외부 감지
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        // 검색어 변경 시 필터링
        if (searchTerm) {
            const filtered = sites.filter(site => {
                const search = searchTerm.toLowerCase();
                return site.name.toLowerCase().includes(search) ||
                       (site.address && site.address.toLowerCase().includes(search));
            });
            setFilteredSites(filtered);
        } else {
            setFilteredSites(sites);
        }
        setSelectedIndex(-1);
    }, [searchTerm, sites]);

    const fetchSites = async () => {
        setLoading(true);
        try {
            const [siteResponse, myInfoResponse] = await Promise.all([
                staffClient.get('/site', { params: { limit: 1000 } }),
                staffClient.get('/auth/my-info'),
            ]);

            const userType = myInfoResponse.data?.token?.type || '';
            const allSites = siteResponse.data?.data || [];
            let visibleSites = allSites.filter((site: any) => site.visible !== false);

            // STAFF는 본인에게 배정된 현장만 검색 가능
            if (userType === 'STAFF') {
                try {
                    const mySiteIds: number[] = await staffClient.get('/auth/my-sites').then(r => r.data);
                    visibleSites = visibleSites.filter((site: any) => mySiteIds.includes(site.id));
                } catch {
                    visibleSites = [];
                }
            }

            const sitesData: Site[] = visibleSites.map((site: any, index: number) => {
                // Handle progressRate from server
                let progressValue = 0;
                if (site.progressRate !== undefined && site.progressRate !== null) {
                    if (typeof site.progressRate === 'string') {
                        progressValue = parseInt(site.progressRate.replace('%', '')) || 0;
                    } else {
                        progressValue = Number(site.progressRate) || 0;
                    }
                }

                // Determine status based on type field (from server)
                let status: 'active' | 'completed' | 'upcoming' = 'active';

                if (site.type === 'COMPLETED') {
                    status = 'completed';
                } else if (site.type === 'CONTRACT_COMPLETED') {
                    status = 'upcoming';
                } else if (site.type === 'IN_PROGRESS') {
                    status = 'active';
                } else if (site.type === 'ETC') {
                    status = 'active';
                }

                return {
                    id: site.id || index + 1,
                    name: site.name || `현장 ${index + 1}`,
                    address: site.location || site.address || '위치 정보 없음',
                    location: site.location,
                    status: status,
                    type: site.type,
                    progressRate: site.progressRate,
                    progress: progressValue
                };
            });

            setSites(sitesData);
            setFilteredSites(sitesData);
        } catch (error) {
            console.error('Failed to fetch sites:', error);
            setSites([]);
            setFilteredSites([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSiteSelect = (site: Site) => {
        setSearchTerm('');
        setIsOpen(false);
        
        if (onSiteChange) {
            onSiteChange(site.id);
        } else {
            // 현재 쿼리 파라미터 유지하면서 현장 상세 페이지로 이동
            const currentParams = new URLSearchParams(location.search);
            const queryString = currentParams.toString();
            navigate(`/sites/${site.id}${queryString ? `?${queryString}` : ''}`);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen && e.key !== 'Escape') {
            setIsOpen(true);
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < filteredSites.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredSites.length) {
                    handleSiteSelect(filteredSites[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                inputRef.current?.blur();
                break;
        }
    };

    const getStatusBadge = (status: string) => {
        const badges = {
            active: { text: '진행중', class: 'status-active' },
            upcoming: { text: '예정', class: 'status-upcoming' },
            completed: { text: '완료', class: 'status-completed' }
        };
        return badges[status as keyof typeof badges] || { text: status, class: '' };
    };

    const currentSite = sites.find(s => s.id.toString() === currentSiteId);

    return (
        <div className="site-search-bar" ref={searchRef}>
            <div className="search-input-wrapper">
                <svg className="search-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder={currentSite ? `현재: ${currentSite.name}` : "현장명 또는 주소로 검색..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />
                {sites.length > 0 && (
                    <span className="site-count">전체 {sites.length}개</span>
                )}
            </div>

            {isOpen && (
                <div className="search-dropdown">
                    {loading ? (
                        <div className="dropdown-loading">
                            <div className="spinner"></div>
                            <span>현장 정보를 불러오는 중...</span>
                        </div>
                    ) : filteredSites.length > 0 ? (
                        <div className="dropdown-list">
                            {searchTerm === '' && (
                                <div className="dropdown-header">빠른 현장 이동</div>
                            )}
                            {filteredSites.map((site, index) => (
                                <div
                                    key={site.id}
                                    className={`dropdown-item ${selectedIndex === index ? 'selected' : ''} ${
                                        currentSiteId === site.id.toString() ? 'current' : ''
                                    }`}
                                    onClick={() => handleSiteSelect(site)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <div className="site-info">
                                        <div className="site-name">
                                            {site.name}
                                            <span className={`site-status ${getStatusBadge(site.status).class}`}>
                                                {getStatusBadge(site.status).text}
                                            </span>
                                            {currentSiteId === site.id.toString() && (
                                                <span className="current-badge">현재</span>
                                            )}
                                        </div>
                                        <div className="site-meta">
                                            <span className="site-address">{site.address}</span>
                                        </div>
                                    </div>
                                    <svg className="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="dropdown-empty">
                            <svg className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p>검색 결과가 없습니다</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SiteSearchBar;